// fleet-generated: do not edit by hand
//
// Copied verbatim  : infra/fleet/artifacts/powersync/guest-flush.ts
// Parameters       : none — this file is byte-identical across the fleet
// Destination      : packages/fleet/powersync/src/guest-flush.ts
//
// TO CHANGE THIS FILE, edit the canonical copy in infra and re-render. It
// changes for the whole fleet at once. Editing it here is reverted by the
// next render, and the guard in this repo goes red in the meantime:
//
//     cd ../infra && node fleet/fleet.ts render --repo gym-log-app
//     node fleet/fleet.ts check --repo gym-log-app
//
// The generator runs LOCALLY and is never invoked by CI. CI only verifies
// that what is committed matches the hash in .github/fleet-lock.json, which
// also records WHICH infra commit produced it. The commit is deliberately
// not repeated here: one value, one place — and stamping it into every file
// meant a one-line template edit rewrote four files in 28 repos.
/**
 * Guest → account upgrade: upload the guest scratch DB's queued writes, then
 * clear the scratch so the rows live only in the account (no duplicates in a
 * later "Continue as guest").
 *
 * The scratch is cleared ONLY once its upload queue is confirmed empty. Before
 * this helper, every product cleared it in a `finally` after a 15 s deadline,
 * so a slow or failing upload (or a throwing `connect`) wiped writes that had
 * never reached the server. Keeping the rows costs a possible duplicate;
 * clearing them costs the user's data, and that cannot be undone.
 *
 * The helper knows nothing about PowerSync: the caller passes the four
 * operations, which keeps it testable without a database.
 */

export interface GuestFlushTarget {
  /** Rows still waiting in the upload queue (`ps_crud`). Only `0` counts as drained. */
  pendingCount(): Promise<number>;
  /**
   * One upload attempt. A transient failure may throw: it is retried until the
   * deadline. Omit it when the DB is connected and uploads on its own.
   */
  upload?(): Promise<void>;
  /** Wipe the scratch DB and its queue (`disconnectAndClear`). */
  clear(): Promise<void>;
  /** Stop syncing without touching the data (`disconnect`). */
  disconnect(): Promise<void>;
}

export interface GuestFlushOptions {
  deadlineMs?: number;
  pollMs?: number;
  /** Injected in tests. */
  now?: () => number;
  /** Injected in tests. */
  sleep?: (ms: number) => Promise<void>;
  onWarning?: (message: string, error: unknown) => void;
}

/**
 * `cleared`: everything uploaded and the scratch was wiped.
 * `uploaded-not-cleared`: everything uploaded, but wiping failed (a duplicate, never a loss).
 * `kept`: the queue did not drain; the scratch still holds the rows for a later retry.
 */
export type GuestFlushOutcome = "cleared" | "uploaded-not-cleared" | "kept";

const DEFAULT_DEADLINE_MS = 15_000;
const DEFAULT_POLL_MS = 250;

const defaultSleep = (ms: number) =>
  new Promise<void>((resolve) => {
    setTimeout(resolve, ms);
  });

export async function flushGuestScratch(
  target: GuestFlushTarget,
  options: GuestFlushOptions = {},
): Promise<GuestFlushOutcome> {
  const now = options.now ?? Date.now;
  const sleep = options.sleep ?? defaultSleep;
  const warn = options.onWarning ?? ((message, error) => console.warn(message, error));
  const deadline = now() + (options.deadlineMs ?? DEFAULT_DEADLINE_MS);

  let drained = false;
  for (;;) {
    try {
      // oxlint-disable-next-line no-await-in-loop -- polling is sequential by design
      drained = (await target.pendingCount()) === 0;
    } catch (error) {
      // A count we cannot read is not an empty queue.
      warn("[guest-flush] could not read the upload queue", error);
      drained = false;
    }
    if (drained || now() >= deadline) {
      break;
    }
    if (target.upload) {
      try {
        // oxlint-disable-next-line no-await-in-loop -- one upload at a time
        await target.upload();
      } catch (error) {
        warn("[guest-flush] upload failed, retrying", error);
      }
    }
    // oxlint-disable-next-line no-await-in-loop -- polling is sequential by design
    await sleep(options.pollMs ?? DEFAULT_POLL_MS);
  }

  if (drained) {
    try {
      await target.clear();
      return "cleared";
    } catch (error) {
      warn("[guest-flush] could not clear the guest scratch", error);
    }
  }
  try {
    await target.disconnect();
  } catch (error) {
    warn("[guest-flush] could not disconnect the guest scratch", error);
  }
  return drained ? "uploaded-not-cleared" : "kept";
}
